import { useTranslation } from 'react-i18next'
import type { ExpiryStatus, LicenseInReport } from '../types'

const EXPIRY_RED_DAYS = 14
const EXPIRY_AMBER_DAYS = 30

function daysColourClass(days: number): string {
  if (days < 0) return 'text-stone-500'
  if (days < EXPIRY_RED_DAYS) return 'text-red-600 dark:text-red-400 font-medium'
  if (days < EXPIRY_AMBER_DAYS) return 'text-amber-600 dark:text-amber-400'
  return 'text-stone-600 dark:text-stone-400'
}

function LicenseDaysCell({ days }: { days: number | null }): React.ReactElement {
  const { t } = useTranslation('admin')
  if (days === null) {
    return <span className="text-blue-600 dark:text-blue-400">{t('licenseReport.perpetual')}</span>
  }
  const label =
    days < 0
      ? t('licenseReport.daysAgo', { count: Math.abs(days) })
      : t('licenseReport.daysRemaining', { count: days })
  return <span className={daysColourClass(days)}>{label}</span>
}

function ExpiryStatusBadge({ status }: { status: ExpiryStatus }): React.ReactElement {
  const { t } = useTranslation('admin')
  const statusConfig: Record<ExpiryStatus, { label: string; className: string }> = {
    ok: {
      label: t('licenseReport.status.ok'),
      className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    },
    warning: {
      label: t('licenseReport.status.warning'),
      className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    },
    critical: {
      label: t('licenseReport.status.critical'),
      className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    },
    expired: {
      label: t('licenseReport.status.expired'),
      className: 'bg-stone-100 text-stone-600 dark:bg-stone-700 dark:text-stone-400',
    },
    perpetual: {
      label: t('licenseReport.status.perpetual'),
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    },
    inactive: {
      label: t('licenseReport.status.inactive'),
      className: 'bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-500',
    },
  }
  const config = statusConfig[status]
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${config.className}`}
    >
      {config.label}
    </span>
  )
}

export function LicenseSeatRows({ license }: { license: LicenseInReport }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="bg-stone-50 px-4 pb-4 dark:bg-stone-800/30">
      {license.seats.length === 0 ? (
        <p className="text-sm text-stone-500 italic dark:text-stone-400">
          {t('licenseReport.noSeatsAssigned')}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 text-left text-stone-500 dark:border-stone-700 dark:text-stone-400">
              <th className="py-2 font-medium">{t('licenseReport.email')}</th>
              <th className="py-2 font-medium">{t('licenseReport.name')}</th>
              <th className="py-2 font-medium">{t('licenseReport.assigned')}</th>
              <th className="py-2 font-medium">{t('labels.status')}</th>
            </tr>
          </thead>
          <tbody>
            {license.seats.map((seat) => (
              <tr
                key={seat.id}
                className="border-b border-stone-100 last:border-b-0 dark:border-stone-700/50"
              >
                <td className="py-2 text-stone-900 dark:text-stone-100">{seat.assignedEmail}</td>
                <td className="py-2 text-stone-600 dark:text-stone-400">{seat.userName ?? '-'}</td>
                <td className="py-2 text-stone-500 dark:text-stone-400">{seat.assignedAt}</td>
                <td className="py-2">
                  {seat.isActive ? (
                    <span className="text-green-600 dark:text-green-400">
                      {t('licenseReport.active')}
                    </span>
                  ) : (
                    <span className="text-stone-400">{t('licenseReport.inactive')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export function LicenseCard({
  license,
  isExpanded,
  onToggle,
}: {
  license: LicenseInReport
  isExpanded: boolean
  onToggle: () => void
}): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-700">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-stone-50 dark:hover:bg-stone-800/50"
      >
        <svg
          className={`h-4 w-4 text-stone-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <div className="grid flex-1 grid-cols-6 items-center gap-4">
          <div className="col-span-2">
            <span className="font-mono text-sm text-stone-900 dark:text-stone-100">
              {license.licenseKey}
            </span>
            {license.accessTier && (
              <span className="ml-2 text-xs text-stone-500 dark:text-stone-400">
                ({license.accessTier})
              </span>
            )}
          </div>
          <div className="text-sm text-stone-600 dark:text-stone-400">
            {license.validUntil ?? t('licenseReport.noExpiry')}
          </div>
          <div className="text-sm">
            <LicenseDaysCell days={license.daysUntilExpiry} />
          </div>
          <div className="text-sm text-stone-600 dark:text-stone-400">
            {t('licenseReport.seatsUsage', { used: license.seatsUsed, total: license.seatsTotal })}
          </div>
          <div>
            <ExpiryStatusBadge status={license.expiryStatus} />
          </div>
        </div>
      </button>
      {isExpanded && <LicenseSeatRows license={license} />}
    </div>
  )
}
